import { FC, useCallback, useRef, useLayoutEffect, useState, useEffect, useMemo } from 'react';
import { Stack, Button, Box, Typography } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { ISecondStepData } from '../content.popup.tsx';
import { TextField, FormSelect } from '../../../../../../../../../shared/components/form';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Theme, Theme as theme } from '../../../../../../../../../shared/styles/theme/theme.ts';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Wa_PriceCategoryMappingBetweenWaAndCaInput } from '../../../../../../../../../graphql/generatedModel.ts';
import { reimportArticlesFromCAState } from '../states/reimportArticlesFromCA.state.ts';
import { Content } from '../../../../../../../../../shared/components/content/content.component.tsx';
import { MessageRotator } from '../../../../../../../../../shared/components/messageRotator/messageRotator.component.tsx';

export const SecondStep: FC<ISecondStep> = ({
  secondStepData: { priceCategories, listPriceCategoriesMappingBetweenWAAndCA },
}) => {
  const { t } = useTranslation();
  const refForm = useRef<any>(null);
  const refWrap = useRef<any>(null);
  const [arrowCenterSize, setArrowCenterSize] = useState<number | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const generateDefaultValues = useCallback(() => {
    return listPriceCategoriesMappingBetweenWAAndCA.reduce<{
      [key: string]: string | undefined | null;
    }>((acc, el) => {
      acc[
        `${el.caCategoryName || 'empty'}_${el.caCategoryId}_${
          el.caCategoryIsCafe ? TYPE_CA_PRICE_CATEGORY.CAFE : TYPE_CA_PRICE_CATEGORY.NORMAL
        }`
      ] = el.waCategoryId;
      return acc;
    }, {});
  }, []);

  useLayoutEffect(() => {
    const formHeight = refForm?.current?.getBoundingClientRect()?.height;
    const wrapHeight = refWrap?.current?.getBoundingClientRect()?.height;
    if (typeof formHeight === 'number' && typeof wrapHeight === 'number') {
      if (formHeight >= wrapHeight) {
        setArrowCenterSize(wrapHeight / 2);
      } else setArrowCenterSize(formHeight / 2);
    }
  }, [refForm, refWrap]);

  const formMethods = useForm({ defaultValues: generateDefaultValues() });

  const handleClick = () => {
    const rowData = formMethods.getValues();
    const preparedDataForSave: Array<Wa_PriceCategoryMappingBetweenWaAndCaInput> = [];
    for (const rowField in rowData) {
      const separatedField = rowField.split('_');
      preparedDataForSave.push({
        caCategoryId: separatedField[1],
        caCategoryIsCafe: separatedField[2] === TYPE_CA_PRICE_CATEGORY.CAFE,
        waCategoryId: rowData[rowField],
      });
    }
    reimportArticlesFromCAState.pub.saveData(preparedDataForSave);
  };

  useEffect(() => {
    const loadingSub = reimportArticlesFromCAState.sub.secondPageLoading().subscribe(setLoading);
    return () => {
      loadingSub.unsubscribe();
    };
  }, []);

  const messageRotator = useMemo(() => {
    const messagesForLongLoading = [
      t('settings.import_saving_mapping'),
      t('settings.import_starting'),
      t('settings.import_processing_rows'),
      t('settings.import_validating_data'),
      t('settings.import_applying_changes'),
    ];
    return <MessageRotator uniqueMessages={messagesForLongLoading} />;
  }, []);

  return (
    <Content type='box' {...{ loading, messageRotator }}>
      <Box sx={{ ...(loading && { opacity: '0' }) }}>
        <Stack sx={{ paddingRight: '10px', paddingLeft: '10px' }}>
          <Typography
            variant='caption'
            sx={{
              fontWeight: 600,
              fontSize: 24,
              marginBottom: 2,
              lineHeight: 1.1,
            }}
          >
            {t('settings.import_mapping')}
          </Typography>
          <Typography
            variant='caption'
            sx={{
              fontWeight: 400,
              fontSize: 14,
              marginBottom: 2,
            }}
          >
            {t('settings.subtitle_import_step_2')}
          </Typography>
        </Stack>
        <Box sx={{ position: 'relative' }}>
          <Stack
            ref={refWrap}
            height='calc(100vh - 350px)'
            sx={{
              overflowY: 'scroll',
            }}
          >
            <FormProvider {...formMethods}>
              <SForm noValidate ref={refForm}>
                <Stack
                  sx={{
                    position: 'sticky',
                    top: 0,
                    mb: 3,
                    zIndex: 1000,
                  }}
                  direction='row'
                  gap={9}
                  mb={1}
                >
                  <Typography
                    fontWeight={600}
                    fontSize={16}
                    color={theme.palette.primary.main}
                    sx={{ width: '50%', backgroundColor: Theme.palette.background.default, pb: 1 }}
                  >
                    {t('settings.cashassist_price_categories')}
                  </Typography>
                  <Typography
                    fontWeight={600}
                    fontSize={16}
                    color={theme.palette.primary.main}
                    sx={{ width: '50%', backgroundColor: Theme.palette.background.default, pb: 1 }}
                  >
                    {t('settings.wawiassist_price_categories')}
                  </Typography>
                </Stack>
                <Stack gap={3}>
                  {listPriceCategoriesMappingBetweenWAAndCA
                    .sort((a, b) => {
                      if (a.caCategoryId !== b.caCategoryId) {
                        return Number(a.caCategoryId) - Number(b.caCategoryId);
                      }
                      return a.caCategoryIsCafe === b.caCategoryIsCafe
                        ? 0
                        : a.caCategoryIsCafe
                        ? 1
                        : -1;
                    })
                    .map((el, i) => (
                      <Stack direction='row' gap={9} key={i}>
                        <TextField
                          width='50%'
                          label={`${t('customer.priceCategories')} ${el.caCategoryId}${
                            el.caCategoryIsCafe ? ' Im Haus' : ''
                          }`}
                          InputProps={{
                            readOnly: true,
                          }}
                          value={el.caCategoryName}
                        />
                        <FormSelect
                          width='50%'
                          label={`${t('common.category')} WawiAssist`}
                          name={`${el.caCategoryName || 'empty'}_${el.caCategoryId}_${
                            el.caCategoryIsCafe
                              ? TYPE_CA_PRICE_CATEGORY.CAFE
                              : TYPE_CA_PRICE_CATEGORY.NORMAL
                          }`}
                          options={priceCategories as []}
                        />
                      </Stack>
                    ))}
                </Stack>
              </SForm>
            </FormProvider>
            <SArrow arrowcentersize={arrowCenterSize}>
              <ArrowForwardIcon sx={{ color: Theme.palette.grey['300'], top: 10 }} />
            </SArrow>
          </Stack>
          <Stack direction='row' justifyContent='right' sx={{ paddingRight: '10px', mt: 4 }}>
            <Button
              sx={{ minWidth: '150px' }}
              variant='contained'
              onClick={handleClick}
              disabled={!listPriceCategoriesMappingBetweenWAAndCA.some(
                item => formMethods.watch(
                  `${item.caCategoryName || 'empty'}_${item.caCategoryId}_${
                  item.caCategoryIsCafe
                    ? TYPE_CA_PRICE_CATEGORY.CAFE
                    : TYPE_CA_PRICE_CATEGORY.NORMAL
                }`))}
            >
              {t('common.import')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Content>
  );
};

interface ISecondStep {
  secondStepData: ISecondStepData;
}

const SForm = styled('form')({
  width: '100%',
  position: 'relative',
  paddingRight: 10,
  paddingLeft: 10,
  '&::after': {
    content: '""',
    display: 'block',
    width: '1px',
    height: '100%',
    backgroundColor: Theme.palette.grey['300'],
    position: 'absolute',
    top: 1,
    left: '50%',
    transform: 'translateX(-50%)',
  },
});

const SArrow = styled(Stack)<{ arrowcentersize?: number }>(({ arrowcentersize }) => ({
  position: 'absolute',
  top: arrowcentersize ? `${arrowcentersize}px` : '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: Theme.palette.background.default,
  height: 60,
  justifyContent: 'center',
  alignItems: 'center',
}));

enum TYPE_CA_PRICE_CATEGORY {
  CAFE = 'Cafe',
  NORMAL = 'Normal',
}
